/**
 * 학생 화면이 쓰는 서버 응답 형식(growing-server v0.4.0 학생용 API, docs/api-format-v0.4.0.md).
 * 화면은 정답·정답 인정 목록·정답 개수·검수 단어 연결을 받지도 쓰지도 않는다.
 */

export interface Me {
  readonly id: string;
  readonly role: 'student' | 'teacher' | 'director';
  readonly status: 'pending' | 'approved' | 'rejected' | 'suspended';
  readonly name: string | null;
}

export type Segment = { readonly kind: 'text'; readonly text: string } | { readonly kind: 'point'; readonly pointId: string };

export interface GrOptionView { readonly id: string; readonly order: number; readonly label: string }
export interface GrPointView { readonly id: string; readonly order: number; readonly displayNumber: string; readonly options: readonly GrOptionView[] }

export interface ItemView {
  readonly id: string;
  /** 배포 판 안의 정렬 순서. 화면에 숫자로 보이지 않는다. */
  readonly order: number;
  /** 표시용 분류. 화면 분기는 responseMode로만 한다. */
  readonly type: string;
  readonly responseMode: 'single-choice' | 'gr-points';
  /** 원 자료의 쪽 번호 */
  readonly page: number | null;
  /** 원 문항 번호 */
  readonly number: number;
  /** 문항 출처 표시(자유 문구). 구간명 전용 필드가 아니다. */
  readonly sourceLabel: string | null;
  /** 구간명(강·단원·지문 묶음). 선택 필드 */
  readonly sectionLabel: string | null;
  readonly passageId: string | null;
  readonly stem: string | null;
  readonly options: readonly string[];
  readonly segments: readonly Segment[] | null;
  readonly grPoints: readonly GrPointView[];
}

export type AttemptStatus = 'in-progress' | 'submitted' | 'void';

export interface SavedResponse {
  readonly itemId: string;
  readonly state: 'answered' | 'unanswered' | 'input-error' | 'dont-know';
  readonly value: number | null;
  readonly revision: number;
  readonly channel: 'screen' | 'omr';
  readonly omrRaw: string | null;
}
export interface SavedGr { readonly pointId: string; readonly selectedOptionId: string | null; readonly selectedOptionIds?: readonly string[]; readonly revision: number }

/** 같은 지점 복수 선택은 화면 값(string)에 정렬된 보기 id를 ','로 이어 붙여 둔다(보기 id는 UUID라 ','를 갖지 않는다). */
export const GR_SEP = ',';
export function grValueFromSaved(g: { selectedOptionId?: string | null; selectedOptionIds?: readonly string[] | null } | undefined): string | null {
  const ids = g?.selectedOptionIds;
  if (ids !== undefined && ids !== null && ids.length > 0) return [...new Set(ids)].sort().join(GR_SEP);
  return g?.selectedOptionId ?? null;
}
export function grIdsOf(v: string | null): string[] { return v === null || v === '' ? [] : v.split(GR_SEP); }

export interface ItemsResponse {
  readonly attemptId: string;
  readonly status: AttemptStatus;
  readonly assignment: { readonly id: string; readonly title: string; readonly allowUnanswered: boolean; readonly dueAt: string | null; readonly inputChannels: readonly InputChannel[] };
  readonly release: { readonly title: string; readonly version: number; readonly sourceLabel: string | null };
  readonly items: readonly ItemView[];
  readonly saved: readonly SavedResponse[];
  readonly grSaved: readonly SavedGr[];
  /** 지점별 선택 방식. 'multiple'이면 같은 지점에서 여러 보기를 고른다(없으면 모두 single). */
  readonly pointModes?: Readonly<Record<string, 'single' | 'multiple'>>;
}

/**
 * 일반 객관식 화면 값. 'blank' = 고르지 않음(선택 지우기).
 * 'other' = 이 화면이 만들지 않는 서버 저장 상태(다른 경로의 OMR 입력 오류·모름 등). 표시만 하고 이 화면에서 그 값으로 저장하지 않는다.
 */
export type ChoiceValue =
  | { readonly kind: 'choice'; readonly value: number }
  | { readonly kind: 'blank' }
  /** 종이 OMR 번호 입력(경로 omr). raw는 학생이 입력한 원본 그대로 */
  | { readonly kind: 'omr'; readonly raw: string }
  | { readonly kind: 'other'; readonly state: string; readonly raw: string | null };
/** GR 화면 값: 보기 ID 하나 또는 미선택(null). */
export type GrValue = string | null;
export type TargetValue = ChoiceValue | GrValue;

export function sameValue(a: TargetValue, b: TargetValue): boolean {
  if (a === null || b === null || typeof a === 'string' || typeof b === 'string') return a === b;
  if (a.kind === 'blank' || b.kind === 'blank') return a.kind === b.kind;
  if (a.kind === 'omr' || b.kind === 'omr') return a.kind === 'omr' && b.kind === 'omr' && a.raw === b.raw;
  if (a.kind === 'choice' || b.kind === 'choice') return a.kind === 'choice' && b.kind === 'choice' && a.value === b.value;
  return a.state === b.state && a.raw === b.raw;
}

export function isEmptyValue(v: TargetValue): boolean {
  return v === null || (typeof v === 'object' && (v.kind === 'blank' || (v.kind === 'omr' && v.raw.trim() === '')));
}

export type InputChannel = 'screen' | 'omr';

/** 학생용 과제 목록·정보(GET /assignments, GET /assignments/:id) */
export interface AssignmentInfo {
  readonly id: string;
  readonly title: string;
  /** responseModes: 배포 판의 응답 방식 요약(서버 v0.6.1, 정답·보기 내용 없음). 고정 순서 single-choice → gr-points */
  readonly release: { readonly title: string; readonly version: number; readonly sourceLabel: string | null; readonly responseModes?: readonly ('single-choice' | 'gr-points')[] };
  readonly dueAt: string | null;
  readonly allowUnanswered: boolean;
  readonly inputChannels: readonly InputChannel[];
  readonly progress: { readonly status: 'not-started' | AttemptStatus; readonly attemptId: string | null; readonly submittedAt: string | null };
  /** 다음 동작(서버가 정함): 풀기 시작 · 이어 풀기 · 없음(제출 완료 등) */
  readonly action: 'start' | 'resume' | 'none';
}
